import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useQuery } from '@tanstack/react-query';
import { 
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  Typography,
  Alert,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Popper,
} from '@mui/material';
import { 
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as UrgentIcon,
  People as PeopleIcon,
  BuildCircle as BuildIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  DeleteSweep as DeleteSweepIcon,
  Edit as EditIcon,
  Money as MoneyIcon,
  Money,
} from '@mui/icons-material';
import HashtagFilter from './HashtagFilter';
import TaskAssistantSuggester from './TaskAssistantSuggester';
import { useAuth } from '../contexts/AuthContext';


interface User {
    _id: string;
    name: string;
    email: string;
  }  

  interface Personnel {
    _id: string;
    name: string;
    email: string;
  }

interface Strand {
  _id: string;
  name: string;
  events: string[];
}

interface Event {
  _id: string;
  name: string;
  strandId: string;
  performances: string[];
}

interface Performance {
  _id: string;
  name: string;
  eventId: string;
}

interface Task {
  _id: string;
  text: string;
  tags: string[];  // Add this line
  completed: boolean;
  urgent: boolean;
  created: Date;
  createdBy?: string;
  assignedTo?: string;
  strandId?: string;
  eventId?: string;
  performanceId?: string;
}

interface Suggestion {
  type: 'mention' | 'tag';
  text: string;
  inputText: string;
  icon: React.ElementType;
}



const EnhancedTaskInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  personnel: Personnel[];
  users: User[];
}> = ({ value, onChange, onSubmit, personnel, users }) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    
  
    const TAGS = [
      { text: '#urgent', inputText: '#u', icon: UrgentIcon },
      { text: '#priority', inputText: '#p', icon: UrgentIcon },
      { text: '#caitriona', inputText: '#c', icon: PeopleIcon },
      { text: '#aoife', inputText: '#a', icon: PeopleIcon },
      { text: '#bianca', inputText: '#b', icon: PeopleIcon },
      { text: '#louis', inputText: '#l', icon: PeopleIcon },
      { text: '#cooler', inputText: '#co', icon: BuildIcon },
      { text: '#email', inputText: '#e', icon: EmailIcon },
      { text: '#phone', inputText: '#ph', icon: PhoneIcon },
      { text: '#programming', inputText: '#pr', icon: BuildIcon },
      { text: '#finance', inputText: '#f', icon: MoneyIcon },
    ];
  
    useEffect(() => {
      setInputValue(value);
    }, [value]);
  
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      onChange(newValue);
  
      const cursorPosition = event.target.selectionStart || 0;
      const textBeforeCursor = newValue.slice(0, cursorPosition);
      const words = textBeforeCursor.split(/\s/);
      const currentWord = words[words.length - 1];
  
      setAnchorEl(event.currentTarget);
  
      if (currentWord.startsWith('@')) {
        const search = currentWord.slice(1).toLowerCase();
        const allPeople = [
          ...personnel.map(p => ({ id: p._id, name: p.name })),
          ...users.map(u => ({ id: u._id, name: u.name }))
        ];
        
        const mentionSuggestions = allPeople
          .filter(p => p.name.toLowerCase().includes(search))
          .map(p => ({
            type: 'mention' as const,
            text: `@${p.name}`,
            inputText: `@${p.name.split(' ')[0]}`,
            icon: PersonIcon,
          }));
        setSuggestions(mentionSuggestions);
      } else if (currentWord.startsWith('#')) {
        const search = currentWord.slice(1).toLowerCase();
        const tagSuggestions: Suggestion[] = TAGS.filter(tag => 
          tag.text.toLowerCase().includes(search) ||
          tag.inputText.toLowerCase().includes(search)
        ).map(tag => ({
          type: 'tag',
          text: tag.text,
          inputText: tag.inputText,
          icon: tag.icon,
        }));
        setSuggestions(tagSuggestions);
      } else {
        setSuggestions([]);
      }
    };

    const handleTaskSuggestionSelect = (suggestion: string) => {
      // Replace the current word with the suggestion
      const words = inputValue.split(' ');
      words[words.length - 1] = suggestion + ' ';
      const newValue = words.join(' ');
      
      setInputValue(newValue);
      onChange(newValue);
      
      // Focus back to input
      setTimeout(() => {
        const input = document.querySelector('input[placeholder="Add task... (@mention #urgent)"]') as HTMLInputElement;
        if (input) {
          input.focus();
          input.setSelectionRange(newValue.length, newValue.length);
        }
      }, 0);
    };
  
    const handleSuggestionClick = (suggestion: Suggestion) => {
      const words = inputValue.split(/\s/);
      words[words.length - 1] = suggestion.text;
      const newValue = words.join(' ') + ' ';
      setInputValue(newValue);
      onChange(newValue);
      setSuggestions([]);
      setAnchorEl(null);
      
      setTimeout(() => {
        const input = document.querySelector('input[placeholder="Add task... (@mention #urgent)"]') as HTMLInputElement;
        if (input) {
          input.focus();
          input.setSelectionRange(newValue.length, newValue.length);
        }
      }, 0);
    };
  
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !suggestions.length) {
        event.preventDefault();
        onSubmit();
      } else if (event.key === 'Tab' && suggestions.length) {
        event.preventDefault();
        handleSuggestionClick(suggestions[0]);
      }
    };
  
    return (
      <Box sx={{ position: 'relative', width: '100%' }}>
        <TextField
          fullWidth
          size="small"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Add task... (@mention #urgent)"
        />
      <Popper
        open={Boolean(suggestions.length)}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ width: anchorEl?.offsetWidth, zIndex: 1400 }}
      >
        <Paper elevation={3} sx={{ mt: 1 }}>
            {suggestions.map((suggestion, index) => (
              <Box
                key={index}
                sx={{
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <suggestion.icon fontSize="small" />
                <Typography variant="body2">{suggestion.text}</Typography>
              </Box>
            ))}
          </Paper>
        </Popper>
        <TaskAssistantSuggester
        inputValue={inputValue}
        anchorEl={anchorEl}
        onSuggestionSelect={handleTaskSuggestionSelect}
      />
      </Box>
    );
  };

  const EditTaskForm: React.FC<{
    task: Task;
    onSave: (taskId: string, newText: string, newTags: string[]) => void;
    onCancel: () => void;
    personnel: Personnel[];
    users: User[];
  }> = ({ task, onSave, onCancel, personnel, users }) => {
    // Initialize with both text and tags
    const [editText, setEditText] = useState<string>(
      task.text + (task.tags?.length ? ' ' + task.tags.join(' ') : '')
    );
  
    const handleSave = () => {
      // Parse the input text to extract tags
      const hashtags: string[] = (editText.match(/#\w+/g) || []) as string[];
      const cleanText = editText.replace(/#\w+/g, '').trim();
      
      onSave(task._id, cleanText, hashtags);
    };
  
    return (
      <Box sx={{ width: '100%', display: 'flex', gap: 1 }}>
        <EnhancedTaskInput 
          value={editText}
          onChange={setEditText}
          onSubmit={handleSave}
          personnel={personnel}
          users={users}
        />
        <Button onClick={handleSave} color="primary" size="small">Save</Button>
        <Button onClick={onCancel} color="secondary" size="small">Cancel</Button>
      </Box>
    );
  };
  
  
const QuickTasks: React.FC = () => {
  const { user } = useAuth(); // Get current user and role
  const [tasks, setTasks] = useState<Task[]>([]);
  const [newTask, setNewTask] = useState<string>('');
  const [selectedStrand, setSelectedStrand] = useState<string>('all');
  const [selectedEvent, setSelectedEvent] = useState<string>('all');
  const [selectedPerformance, setSelectedPerformance] = useState<string>('all');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/users/me');
      return response.data;
    },
  });
  
  const { data: tasksList = [] } = useQuery<Task[]>({
    queryKey: ['tasks', selectedStrand, user?._id, user?.role],
    queryFn: async () => {
      try {
        // Determine if the user is an admin or team member
        const isAdmin = user?.role === 'admin' || user?.role === 'gm';
        const isTeam = user?.role === 'team';
        
        // Build the endpoint
        let endpoint = '/api/tasks/user'; // Default to user's tasks only
        
        // Admin/team users can use a separate endpoint if they want to
        // view all tasks - but by default, we'll still show just their tasks
        if (isAdmin || isTeam) {
          // You could provide a UI toggle to let admin/team switch between
          // "my tasks" and "all tasks" views, but for now we'll use the filtered view
          endpoint = `/api/tasks/user`;
        }
        
        // Add strand filter if needed
        let params = new URLSearchParams();
        if (selectedStrand !== 'all') {
          params.append('strandId', selectedStrand);
        }
        
        if (params.toString()) {
          endpoint += `?${params.toString()}`;
        }
        
        const response = await axiosInstance.get(endpoint);
        console.log('Tasks fetched:', response.data);
        return response.data;
      } catch (error) {
        console.error('Error fetching tasks:', error);
        throw error;
      }
    },
    enabled: !!user?._id, // Only run when we have a user ID
  });
  
  
  // Replace the tasks state with the filtered results
  useEffect(() => {
    setTasks(tasksList);
  }, [tasksList, selectedStrand]);

  const editTask = async (taskId: string, newText: string, newTags: string[]) => {
    try {
      const response = await axiosInstance.patch(`/api/tasks/${taskId}`, {
        text: newText,
        tags: newTags,
        urgent: newTags.includes('#urgent')
      });
  
      setTasks(tasks.map(task => 
        task._id === taskId ? response.data : task
      ));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };



  const { data: strands = [] } = useQuery<Strand[]>({
    queryKey: ['strands'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/strands');
      return response.data;
    },
  });

  const { data: events = [] } = useQuery<Event[]>({
    queryKey: ['events', selectedStrand],
    queryFn: async () => {
      if (selectedStrand === 'all') return [];
      const response = await axiosInstance.get(`/api/events?strandId=${selectedStrand}`);
      return response.data;
    },
    enabled: selectedStrand !== 'all',
  });

  const { data: performances = [] } = useQuery<Performance[]>({
    queryKey: ['performances', selectedEvent],
    queryFn: async () => {
      if (selectedEvent === 'all') return [];
      const response = await axiosInstance.get(`/api/performances?eventId=${selectedEvent}`);
      return response.data;
    },
    enabled: selectedEvent !== 'all',
  });

  const { data: personnel = [] } = useQuery<Personnel[]>({
    queryKey: ['personnel'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/personnel');
      console.log('Personnel response:', response.data);  // Add this log
      return response.data;
    },
    enabled: !!currentUser,  // Only fetch when we have a user
});

    // Add query for users
    const { data: users = [] } = useQuery<User[]>({
        queryKey: ['users'],
        queryFn: async () => {
          const response = await axiosInstance.get('/api/users');
          return response.data;
        },
      });

       // Create a map of all possible assignees combining personnel and users
// Create a map of all possible assignees combining personnel and users
const assigneeMap = useMemo(() => {
    // Don't create map until we have both data sources
    if (!personnel?.length && !users?.length) return new Map();
    
    const map = new Map<string, string>();
    
    personnel?.forEach((p: Personnel) => {
      map.set(p._id, p.name);
    });
    
    users?.forEach((u: User) => {
      if (!map.has(u._id)) {
        map.set(u._id, u.name);
      }
    });
    
    return map;
}, [personnel, users]);
  
  const getAssigneeName = (assignedToId?: string | any): string => {
    if (!assignedToId) return 'Unassigned';
    
    // If assignedToId is an object with _id property, use that
    const id = typeof assignedToId === 'object' ? assignedToId._id : assignedToId;
    
    console.log('Looking up ID:', id, 'in map:', Array.from(assigneeMap.entries()));
    return assigneeMap.get(id) || 'Unknown';
};
// Replace your existing filtering code with this:
const { activeTasks, completedTasks } = useMemo(() => {
  // First filter by tags if any are selected
  const filteredTasks = selectedTags.length > 0
    ? tasks.filter(task => {
        // A task matches if it has at least one of the selected tags
        return selectedTags.some(tag => (task.tags || []).includes(tag));
      })
    : tasks;
  
  // Then separate into active and completed
  return filteredTasks.reduce(
    (acc, task) => {
      if (task.completed) {
        acc.completedTasks.push(task);
      } else {
        acc.activeTasks.push(task);
      }
      return acc;
    },
    { activeTasks: [] as Task[], completedTasks: [] as Task[] }
  );
}, [tasks, selectedTags]);

  // Add function to delete all completed tasks
  const deleteCompletedTasks = async () => {
    try {
      const deletePromises = completedTasks.map(task => 
        axiosInstance.delete(`/api/tasks/${task._id}`)
      );
      await Promise.all(deletePromises);
      setTasks(tasks.filter(task => !task.completed));
    } catch (error) {
      console.error('Failed to delete completed tasks:', error);
    }
  };
  

  useEffect(() => {
    setSelectedEvent('all');
    setSelectedPerformance('all');
  }, [selectedStrand]);

  useEffect(() => {
    setSelectedPerformance('all');
  }, [selectedEvent]);

  const parseTaskInput = (input: string): Partial<Task> => {
    // Extract all hashtags
    const hashtags: string[] = (input.match(/#\w+/g) || []) as string[];
    
    // Extract mention first (before we modify the text)
    const assignMatch = input.match(/@(\w+\s*\w*)/);
    const mentionedName = assignMatch ? assignMatch[1].toLowerCase() : null;
    
    // Clean the text by removing BOTH hashtags AND mentions
    let cleanText = input.replace(/#\w+/g, '').trim();
    cleanText = cleanText.replace(/@\w+\s*\w*/g, '').trim();
    
    const task: Partial<Task> = {
      text: cleanText,
      tags: hashtags,
      completed: false,
      urgent: hashtags.includes('#urgent'),
      created: new Date(),
      assignedTo: currentUser?._id, // Default to current user
      createdBy: currentUser?._id,
    };
    
    // Apply the user assignment if we found a mention
    if (mentionedName) {
      // Check both personnel and users
      const assignedPerson = personnel.find(p => 
        p.name.toLowerCase().includes(mentionedName)
      );
      
      const assignedUser = users.find(u => 
        u.name.toLowerCase().includes(mentionedName)
      );
  
      if (assignedPerson) {
        task.assignedTo = assignedPerson._id;
      } else if (assignedUser) {
        task.assignedTo = assignedUser._id;
      }
    }
    
    if (selectedStrand !== 'all') task.strandId = selectedStrand;
    if (selectedEvent !== 'all') task.eventId = selectedEvent;
    if (selectedPerformance !== 'all') task.performanceId = selectedPerformance;
  
    return task;
  };

  const addTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTask.trim()) return;

    const task = parseTaskInput(newTask);
    
    try {
      const response = await axiosInstance.post('/api/tasks', task);
      setTasks(prev => [response.data, ...prev]);
      setNewTask('');
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const toggleComplete = async (taskId: string) => {
    try {
      const task = tasks.find(t => t._id === taskId);
      if (!task) return;

      const response = await axiosInstance.patch(`/api/tasks/${taskId}`, {
        completed: !task.completed
      });

      setTasks(tasks.map(task => 
        task._id === taskId ? response.data : task
      ));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const deleteTask = async (taskId: string) => {
    try {
      await axiosInstance.delete(`/api/tasks/${taskId}`);
      setTasks(tasks.filter(task => task._id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 800, mx: 'auto', my: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Quick Tasks</Typography>
        {completedTasks.length > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<DeleteSweepIcon />}
            onClick={deleteCompletedTasks}
            size="small"
          >
            Delete Completed ({completedTasks.length})
          </Button>
        )}
      </Box>

      <HashtagFilter 
  tasks={tasks} 
  selectedTags={selectedTags} 
  onTagsChange={setSelectedTags}
/>

      <form onSubmit={addTask}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Strand</InputLabel>
              <Select
                value={selectedStrand}
                onChange={(e) => setSelectedStrand(e.target.value)}
                label="Strand"
              >
                <MenuItem value="all">All Strands</MenuItem>
                {strands.map((strand: Strand) => (
                  <MenuItem key={strand._id} value={strand._id}>
                    {strand.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>



            {selectedStrand !== 'all' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Event</InputLabel>
                <Select
                  value={selectedEvent}
                  onChange={(e) => setSelectedEvent(e.target.value)}
                  label="Event"
                >
                  <MenuItem value="all">All Events</MenuItem>
                  {events.map((event: Event) => (
                    <MenuItem key={event._id} value={event._id}>
                      {event.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedEvent !== 'all' && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Performance</InputLabel>
                <Select
                  value={selectedPerformance}
                  onChange={(e) => setSelectedPerformance(e.target.value)}
                  label="Performance"
                >
                  <MenuItem value="all">All Performances</MenuItem>
                  {performances.map((performance: Performance) => (
                    <MenuItem key={performance._id} value={performance._id}>
                      {performance.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <EnhancedTaskInput
              value={newTask}
              onChange={setNewTask}
              onSubmit={() => addTask({ preventDefault: () => {} } as React.FormEvent)}
              personnel={personnel || []}
              users={users || []}  // Pass users data
            />
            <Button 
              type="submit" 
              variant="contained" 
              size="small"
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Box>
        </Stack>
      </form>



      <Stack spacing={1} sx={{ mt: 2 }}>
        {/* Active Tasks */}
        {activeTasks.map((task: Task) => (
  <TaskItem
    key={task._id}
    task={task}
    onToggle={toggleComplete}
    onDelete={deleteTask}
    onEdit={editTask} // Add this
    getAssigneeName={getAssigneeName}
    strands={strands}
    personnel={personnel || []} // Add this
    users={users || []} // Add this
  />
))}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <>
            <Box sx={{ 
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 2,
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ flex: 1 }}
              >
                Completed Tasks ({completedTasks.length})
              </Typography>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DeleteSweepIcon />}
                onClick={deleteCompletedTasks}
                size="small"
              >
                Clear All
              </Button>
            </Box>
            {completedTasks.map((task: Task) => (
  <TaskItem
    key={task._id}
    task={task}
    onToggle={toggleComplete}
    onDelete={deleteTask}
    onEdit={editTask} // Add this
    getAssigneeName={getAssigneeName}
    strands={strands}
    personnel={personnel || []} // Add this
    users={users || []} // Add this
  />
))}
          </>
        )}
      </Stack>
    </Paper>
  );
};
const TaskItem: React.FC<{
  task: Task;
  onToggle: (id: string) => void;
  onEdit: (id: string, newText: string, newTags: string[]) => void; // Add this
  onDelete: (id: string) => void;
  getAssigneeName: (id?: string) => string;
  strands: Strand[];
  personnel: Personnel[]; // Add this
  users: User[]; // Add this
}> = ({ task, onToggle, onDelete, onEdit, getAssigneeName, strands, personnel, users }) => {
  const [isEditing, setIsEditing] = useState(false);
  const hashtags = task.tags || [];

  const handleEditSave = (taskId: string, newText: string, newTags: string[]) => {
    onEdit(taskId, newText, newTags);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        <EditTaskForm
          task={task}
          onSave={handleEditSave}
          onCancel={() => setIsEditing(false)}
          personnel={personnel}
          users={users}
        />
      </Paper>
    );
  }
  

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: task.completed ? 'action.hover' : 'background.paper',
        opacity: task.completed ? 0.8 : 1,
      }}
    >
      {/* Existing content */}
      <Checkbox
        checked={task.completed}
        onChange={() => onToggle(task._id)}
        size="small"
      />
      
      <Box sx={{ flex: 1 }}>
        {/* Your existing text, tags, and metadata */}
        <Typography
          variant="body1"
          sx={{
            textDecoration: task.completed ? 'line-through' : 'none',
            color: task.completed ? 'text.secondary' : 'text.primary'
          }}
        >
          {task.text}
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, my: 1 }}>
          {hashtags.map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              color={tag === '#urgent' ? 'error' : 
                    tag === '#priority' ? 'warning' :
                    tag === '#cooler' ? 'info' : 'primary'}
              variant="outlined"
            />
          ))}
        </Box>
        
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Existing metadata */}
          <span>
            Owner: {getAssigneeName(task.assignedTo)}
          </span>
          {task.strandId && (
            <span>
              Strand: {strands.find((s: Strand) => s._id === task.strandId)?.name || 'General'}
            </span>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CalendarIcon fontSize="inherit" />
            {new Date(task.created).toLocaleDateString()}
          </Box>
        </Typography>
      </Box>

      {/* Add a container for both edit and delete buttons */}
      <Box sx={{ display: 'flex' }}>
        <IconButton
          size="small"
          onClick={() => setIsEditing(true)}
          sx={{ color: 'text.secondary' }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onDelete(task._id)}
          sx={{ color: 'text.secondary' }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

  export default QuickTasks;